import { skipToken } from "@reduxjs/toolkit/query";
import {
  getOutcomeDefinitionFromPlaybook,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import { IconFlask, IconRefresh } from "@tabler/icons-react";
import { useEffect, useMemo, useState, type FC } from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { useAllPlaybookTestSpecsQuery } from "../../api/entities/playbookTestSpec.js";
import {
  useCreateTeammateAgentTestRunsMutation,
  useGetTeammateAgentTestSpecsResultsQuery,
} from "../../api/entities/teammateAgent.js";
import { appPaths } from "../../app/appPaths.js";
import { getTestResultStatus } from "../../components/recordingAssessmentEditor/feedbackAccuracyHelpers.js";
import { useAppDispatch } from "../../hooks/hook.js";
import { useWaitForAgentPrompt } from "../../hooks/useWaitForAgentPrompt.js";
import {
  NotificationType,
  showNotification,
} from "../notificationsOverlay/notificationsSlice.js";

interface TestStatusPillProps {
  teammateAgentId: string;
  playbookId: PlaybookId | null;
  agentConfigDraftRevisionId: string | null;
  playbookDraftRevisionId: string | null;
  agentConfigHasUnsavedChanges: boolean;
  playbookHasUnsavedChanges: boolean;
  onAgentConfigSaveDraft: () => Promise<void>;
  onPlaybookSaveDraft: () => Promise<void>;
}

export const TestStatusPill: FC<TestStatusPillProps> = ({
  teammateAgentId,
  playbookId,
  agentConfigDraftRevisionId,
  playbookDraftRevisionId,
  agentConfigHasUnsavedChanges,
  playbookHasUnsavedChanges,
  onAgentConfigSaveDraft,
  onPlaybookSaveDraft,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { waitForPromptReady } = useWaitForAgentPrompt({ teammateAgentId });

  // Test-related queries and mutations
  const { data: playbook } = usePlaybookByIdQuery(playbookId ?? skipToken);
  const { data: allTestSpecs } = useAllPlaybookTestSpecsQuery(
    playbookId ? { playbookId } : skipToken,
  );
  // Track if there are in-progress tests to enable polling
  const [hasInProgressTests, setHasInProgressTests] = useState(false);

  const { data: testSpecsResults, refetch: refetchTestSpecsResults } =
    useGetTeammateAgentTestSpecsResultsQuery(
      playbookId ? { teammateAgentId, playbookId } : skipToken,
      { pollingInterval: hasInProgressTests ? 5000 : 0 },
    );
  const [createTeammateAgentTestRuns] =
    useCreateTeammateAgentTestRunsMutation();
  const [isRunningTests, setIsRunningTests] = useState(false);

  // Calculate test status summary
  const testStatusSummary = useMemo(() => {
    if (!testSpecsResults || testSpecsResults.length === 0) {
      return null;
    }

    const outcomeDefinition = getOutcomeDefinitionFromPlaybook(playbook);

    // Count test statuses
    const counts = { passing: 0, failing: 0, inProgress: 0, notRun: 0 };
    for (const result of testSpecsResults) {
      const status = getTestResultStatus(
        result.playbook_test_spec,
        result.playbook_test_result,
        outcomeDefinition,
      );
      if (status === "passing") counts.passing++;
      else if (status === "failing") counts.failing++;
      else if (status === "in_progress") counts.inProgress++;
      else counts.notRun++;
    }

    // Find the most recent test run to get the revision IDs it was tested against.
    // This is used to determine if results are "outdated" (ran on a different revision).
    const mostRecentRun = testSpecsResults
      .map((r) => r.playbook_test_run)
      .filter((run) => run?.created_at != null)
      .sort((a, b) => b!.created_at.localeCompare(a!.created_at))[0];

    return {
      ...counts,
      total: testSpecsResults.length,
      testedAgentConfigRevisionId:
        mostRecentRun?.teammate_agent_config_revision_id ?? null,
      testedPlaybookRevisionId: mostRecentRun?.playbook_revision_id ?? null,
    };
  }, [testSpecsResults, playbook]);

  // Sync hasInProgressTests state to enable/disable RTK Query polling
  useEffect(() => {
    setHasInProgressTests((testStatusSummary?.inProgress ?? 0) > 0);
  }, [testStatusSummary?.inProgress]);

  // Handle running tests - saves any unsaved changes and waits for prompt regeneration first
  const handleRunTests = async () => {
    if (!allTestSpecs || allTestSpecs.length === 0) {
      showNotification(dispatch, {
        id: `no-tests-${Date.now()}`,
        type: NotificationType.FAILURE,
        primaryMessage: "No test cases",
        secondaryMessage: "Add test cases in the Testing tab first.",
      });
      return;
    }

    setIsRunningTests(true);
    try {
      // Save any unsaved changes first
      // This will mark the prompt as stale and enqueue background regeneration
      if (agentConfigHasUnsavedChanges) {
        await onAgentConfigSaveDraft();
      }
      if (playbookHasUnsavedChanges) {
        await onPlaybookSaveDraft();
      }

      // Wait for the prompt to be regenerated before running tests
      const promptReady = await waitForPromptReady();
      if (!promptReady) {
        showNotification(dispatch, {
          id: `prompt-timeout-${Date.now()}`,
          type: NotificationType.FAILURE,
          primaryMessage: "Agent prompt still generating",
          secondaryMessage:
            "Please wait a moment for the agent to finish updating and try again.",
        });
        return;
      }

      // Run all tests
      const allTestSpecIds = allTestSpecs.map((spec) => spec.id);
      await createTeammateAgentTestRuns({
        teammateAgentId,
        playbookTestSpecIds: allTestSpecIds,
      }).unwrap();

      showNotification(dispatch, {
        id: `tests-started-${Date.now()}`,
        type: NotificationType.SUCCESS,
        primaryMessage: "Tests started",
        secondaryMessage: "Running tests against your current configuration.",
      });

      // Refetch to show in-progress status
      refetchTestSpecsResults();
    } catch (error) {
      console.error("Failed to run tests:", error);
      showNotification(dispatch, {
        id: `tests-error-${Date.now()}`,
        type: NotificationType.FAILURE,
        primaryMessage: "Failed to start tests",
        secondaryMessage: "Please try again.",
      });
    } finally {
      setIsRunningTests(false);
    }
  };

  const hasTests = allTestSpecs && allTestSpecs.length > 0;
  const isLoading = isRunningTests || (testStatusSummary?.inProgress ?? 0) > 0;

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center gap-1.5 rounded-full bg-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600">
        <IconRefresh className="h-3.5 w-3.5 animate-spin" />
        Running tests...
      </div>
    );
  }

  // No tests yet - show add tests prompt
  if (!hasTests) {
    return (
      <button
        type="button"
        onClick={() =>
          navigate(appPaths.teammateAgentTesting.build({ teammateAgentId }))
        }
        className="flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-muted-foreground transition-all hover:bg-gray-200 hover:text-gray-700"
        title="Add test cases"
      >
        <IconFlask className="h-3.5 w-3.5" />
        No tests
      </button>
    );
  }

  // Tests exist but haven't been run yet - show run tests button
  if (!testStatusSummary) {
    return (
      <button
        type="button"
        onClick={handleRunTests}
        className="flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 transition-all hover:bg-blue-100"
        title="Run tests"
        aria-label="Run tests"
      >
        <IconFlask className="h-3.5 w-3.5" />
        Run tests
      </button>
    );
  }

  // Has test results - show status with refresh on right
  const { passing, failing, total } = testStatusSummary;
  const allPassing = failing === 0 && passing > 0;
  const hasFailing = failing > 0;

  // Tests are outdated if they were run against a different revision than current
  // (could be older or newer, as long as it's not the same revision).
  // This includes:
  // 1. Tests ran on live (null) but now there's a draft (non-null)
  // 2. Tests ran on a draft but it's a different draft than current
  const testedAgentConfigRevisionId =
    testStatusSummary.testedAgentConfigRevisionId;
  const testedPlaybookRevisionId = testStatusSummary.testedPlaybookRevisionId;

  const isOutdated =
    testedAgentConfigRevisionId !== agentConfigDraftRevisionId ||
    testedPlaybookRevisionId !== playbookDraftRevisionId;

  // Determine styling based on state
  const colors = (() => {
    if (isOutdated) {
      return {
        bg: "bg-gray-200 text-gray-700",
        hover: "hover:bg-gray-300",
      };
    }
    if (hasFailing) {
      return {
        bg: "bg-red-50 text-red-700",
        hover: "hover:bg-red-100",
      };
    }
    if (allPassing) {
      return {
        bg: "bg-green-50 text-green-700",
        hover: "hover:bg-green-100",
      };
    }
    return {
      bg: "bg-gray-200 text-gray-700",
      hover: "hover:bg-gray-300",
    };
  })();

  return (
    <div
      className={twMerge(
        "flex items-center rounded-full text-xs font-medium",
        colors.bg,
      )}
    >
      <button
        type="button"
        onClick={() =>
          navigate(appPaths.teammateAgentTesting.build({ teammateAgentId }))
        }
        className={twMerge(
          "flex items-center gap-1.5 rounded-l-full py-1 pr-1.5 pl-2.5 transition-all",
          colors.hover,
        )}
        title={
          isOutdated
            ? "Tests were run on a previous version — click to view results"
            : "View test results"
        }
      >
        {passing}/{total} tests passing
        {isOutdated && (
          <span className="text-[10px] font-normal opacity-75">· outdated</span>
        )}
      </button>
      <button
        type="button"
        onClick={handleRunTests}
        className={twMerge(
          "flex items-center rounded-r-full py-1 pr-2 pl-1 transition-all",
          colors.hover,
        )}
        title={isOutdated ? "Re-run tests on current draft" : "Re-run tests"}
        aria-label={
          isOutdated ? "Re-run tests on current draft" : "Re-run tests"
        }
      >
        <IconRefresh className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};
